import { useEffect } from 'react';
import { driver, DriveStep } from 'driver.js';
import { useAuth } from '../auth/hooks/auth';
import { useMenuContext } from '../features/sharedComponents/MenuContext';
import { buildAuthorSteps } from './buildAuthorSteps';
import { buildAdminSteps } from './buildAdminSteps';

export const useTour = () => {
  const { user } = useAuth();
  
  // Safely retrieve menu context in case the component using this hook isn't wrapped in MenuProvider
  let adminMenus: any[] = [];
  let menusLoading = false;
  try {
    const context = useMenuContext();
    adminMenus = context.adminMenus;
    menusLoading = context.menusLoading;
  } catch (e) {
    // Context not available (e.g. author dashboard if not wrapped)
  }

  const buildSteps = (): DriveStep[] => {
    if (!user) return [];
    if (user.role === 'author') {
      return buildAuthorSteps(user);
    } else {
      return buildAdminSteps(user, adminMenus);
    }
  };

  const tourKey = user ? `tour_seen_role_${user.roleId}` : '';

  const markTourSeen = () => {
    if (tourKey) {
      localStorage.setItem(tourKey, 'true');
    }
  };

  const hasTourBeenSeen = () => {
    if (!tourKey) return true;
    return localStorage.getItem(tourKey) === 'true';
  };

  const startTour = () => {
    const steps = buildSteps();
    if (steps.length === 0) return;

    const driverObj = driver({
      showProgress: true,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next →',
      prevBtnText: '← Back',
      doneBtnText: 'Finish Tour ✓',
      steps,
      onDestroyStarted: () => {
        markTourSeen();
        driverObj.destroy();
      },
    });

    driverObj.drive();
  };

  // Run automatically on first login or if the tour hasn't been seen for this role
  useEffect(() => {
    if (!user) return;
    
    // For admin users, wait until menus are loaded from the API
    if (user.role !== 'author' && menusLoading) return;

    const shouldStart = user.isInitialLogin || !hasTourBeenSeen();
    if (shouldStart) {
      const timer = setTimeout(() => {
        startTour();
      }, 1500); // 1.5s delay to let the page settle
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.roleId, menusLoading]);

  return { startTour };
};
